import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { DownloadIcon } from 'lucide-react';
import DateRangeCalendar from '../../../components/Calendar';
import { formatDate } from '../../../libs/utils';
import { useAuth } from '../../../hooks/useAuth';
import { useWallet } from '../../../hooks/useWallet';
import { useModal } from '../../../hooks/useModal';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { formatCurrency, formatWalletDate } from '../../../utils/format';
import WalletTrackingNumber from '../../../components/WalletTrackingNumber';

type TransactionType = 'Debit' | 'Credit';

const pageSizeOptions = [
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '50', value: '50' },
];

export default function WalletsPage() {
  const { user } = useAuth();
  const admin = user?.roleName === 'ADMIN';
  const { open, close, Modal } = useModal();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [transactionFilter, setTransactionFilter] = useState<
    TransactionType | 'All'
  >('All');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const isB2BView = useMemo(
    () => location.pathname.startsWith('/b2b'),
    [location.pathname]
  );

  const {
    balance,
    useTransactions,
    getTransactionDetail,
    refetchBalance,
    isLoadingBalance,
  } = useWallet(user?.id as string, isB2BView, user?.roleId);

  useEffect(() => {
    refetchBalance();
  }, [refetchBalance]);

  const queryStartDate = startDate && endDate ? startDate.toISOString() : undefined;
  const queryEndDate = startDate && endDate ? endDate.toISOString() : undefined;

  const { data: transactionResponse, isLoading: isLoadingTransactions } =
    useTransactions(
      currentPage,
      rowsPerPage,
      search,
      transactionFilter === 'All' ? undefined : transactionFilter,
      queryStartDate,
      queryEndDate
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, transactionFilter, queryStartDate, queryEndDate]);

  const resetFilters = () => {
    setSearch('');
    setTransactionFilter('All');
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
  };

  const filteredData = transactionResponse?.transaction_list || [];

  const totalPages = Math.ceil(
    (transactionResponse?.total_count || 0) / rowsPerPage
  );

  const handleDownloadCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error('No transactions available to download');
      return;
    }

    const headers = [
      ...(admin ? ['CRN'] : []),
      'Transaction Ref',
      'Date & Time',
      'Service Type',
      'Tracking Number',
      'Amount',
      'Transaction Type',
      'Description',
    ];

    const csvRows = [
      headers.join(','),
      ...filteredData.map((row) => {
        const category = row.category_name?.toLowerCase();
        const isDebit = row.transaction_name === 'Debit';
        const tracking =
          category === 'recharge' || isDebit
            ? 'N/A'
            : row.tracking_number || 'Bulk Order';

        const fields = [
          ...(admin ? [row.crn_number || ''] : []),
          row.payment_reference_number || row.id || '',
          row.transaction_date_time || '',
          row.category_name || 'N/A',
          tracking,
          `${row.transaction_name === 'Debit' ? '-' : '+'}${row.amount || 0}`,
          row.transaction_name || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
        ];
        return fields.join(',');
      }),
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `wallet_transactions_${isB2BView ? 'b2b' : 'b2c'}_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Transaction history downloaded successfully');
  };

  const handleViewTransaction = async (transactionId: string | number) => {
    try {
      const detail = await getTransactionDetail(transactionId);
      open({
        element: (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-6">Transaction Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">Transaction ID</p>
                <p className="font-semibold">{detail.id}</p>
              </div>
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">Amount</p>
                <p
                  className={`font-bold ${detail.transaction_name === 'Debit' ? 'text-red-600' : 'text-green-600'}`}
                >
                  {detail.transaction_name === 'Debit' ? '- ' : '+ '}₹
                  {detail.amount}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">Type</p>
                <p className="font-semibold">{detail.transaction_name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">Date & Time</p>
                <p className="font-semibold">{formatWalletDate(detail.transaction_date_time)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">CRN</p>
                <p className="font-semibold">{detail.crn_number || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-gray-500 text-sm">Category</p>
                <p className="font-semibold">{detail.category_name || 'N/A'}</p>
              </div>
              <div className="col-span-2 space-y-1">
                <p className="text-gray-500 text-sm">Description</p>
                <p className="font-semibold">{detail.description}</p>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={close}
                className="px-6 py-2 bg-teal text-white rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        ),
        centered: true,
        closable: true,
        className: 'max-w-md w-full',
      });
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 py-4 lg:py-6">
      <div className="mx-auto">
        <h1 className="text-2xl lg:text-3xl font-bold text-text">
          Transaction History
        </h1>
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <span>
            View detailed records of credits, debits, refunds, and adjustments
          </span>
        </div>


        {/* Balance + Download */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 mt-4">
          <span className="text-lg font-medium">
            Available Wallet Balance :
            <span className="font-bold text-text2 ml-1">
              {isLoadingBalance ? (
                <Spinner size="sm" className="inline ml-1" />
              ) : (
                `₹ ${balance || 0}`
              )}
            </span>
          </span>

          <button
            onClick={handleDownloadCSV}
            title="Download CSV"
            className="bg-teal text-white rounded-lg px-3 py-3 hover:bg-teal/90 transition-colors shadow-sm flex items-center gap-2 text-sm font-medium"
          >
            <DownloadIcon size={18} />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 mb-1">
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* Single Unified Search */}
            <Input
              placeholder={
                admin ? 'Search by crn or transaction id or tracking number' : 'Search by or tracking number'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* Debit / Credit Filter */}
            <Select
              className="w-fit"
              value={transactionFilter}
              onChange={(e) =>
                setTransactionFilter(e.target.value as TransactionType | 'All')
              }
              placeholder="All"
              options={[
                { label: 'All', value: 'All' },
                { label: 'Debit', value: 'Debit' },
                { label: 'Credit', value: 'Credit' },
              ]}
            />

            {/* Date Range */}
            <div className="flex flex-col relative" ref={calendarRef}>
              <button
                type="button"
                onClick={() => setShowCalendar((prev) => !prev)}
                className="h-10 border rounded px-3 text-left bg-white min-w-[200px]"
              >
                {startDate && endDate
                  ? `${formatDate(startDate.toISOString())} - ${formatDate(
                    endDate.toISOString()
                  )}`
                  : startDate
                    ? `${formatDate(startDate.toISOString())} - Select end date`
                    : 'Select date range'}
              </button>

              {showCalendar && (
                <div className="absolute top-16 z-50">
                  <DateRangeCalendar
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Reset Button */}
            {(search ||
              transactionFilter !== 'All' ||
              startDate ||
              endDate) && (
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600 self-start h-10"
                >
                  Reset Filters
                </Button>
              )}
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden xl:block overflow-x-auto mt-6">
          {isLoadingTransactions ? (
            <div className="flex items-center justify-center h-40">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="min-w-[1000px] space-y-4">
              {/* Header */}
              <div className="bg-teal border rounded-xl px-6 py-3 font-bold text-white shadow-sm">
                <div
                  className={`grid gap-6 text-center ${admin
                    ? 'grid-cols-[0.8fr_1.5fr_1fr_1fr_1fr_1fr_2fr]'
                    : 'grid-cols-[1.5fr_1fr_1fr_1fr_1fr_2fr]'
                    }`}
                >
                  {admin && <span>CRN</span>}
                  <div>Transaction Details</div>
                  <div>Service Type</div>
                  <div>Tracking Number</div>
                  <div>Amount</div>
                  <div>Transaction Type</div>
                  <div>Description</div>
                </div>
              </div>

              {/* Rows */}
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <div
                    key={row.id}
                    className="bg-card border rounded-xl px-6 py-5 shadow-sm text-center transition-all hover:bg-gray-50"
                  >
                    <div
                      className={`grid gap-6 items-center ${admin
                        ? 'grid-cols-[0.8fr_1.5fr_1fr_1fr_1fr_1fr_2fr]'
                        : 'grid-cols-[1.5fr_1fr_1fr_1fr_1fr_2fr]'
                        }`}
                    >
                      {admin && (
                        <div className="font-medium text-text3">
                          {row.crn_number}
                        </div>
                      )}

                      <div className="group">
                        <div className="font-bold text-text2 text-sm truncate" title={row.payment_reference_number}>
                          {row.payment_reference_number || row.id}
                        </div>
                        <div className="text-sm text-gray-500 font-bold mt-1">
                          {formatWalletDate(row.transaction_date_time)}
                        </div>
                      </div>

                      <div className="text-text font-medium text-sm">
                        {row.category_name || 'N/A'}
                      </div>

                      <div className="text-sm font-medium flex items-center justify-center min-w-0">
                        <WalletTrackingNumber
                          trackingNumber={row.tracking_number}
                          categoryName={row.category_name}
                          transactionName={row.transaction_name}
                        />
                      </div>


                      <div
                        className={`font-bold ${row.transaction_name === 'Debit'
                          ? 'text-red-600'
                          : 'text-green-600'
                          }`}
                      >
                        {row.transaction_name === 'Debit' ? '- ' : '+ '}₹
                        {row.amount}
                      </div>

                      <div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold  ${row.transaction_name === 'Debit'
                            ? 'bg-red-50 text-red-600 border border-red-100'
                            : 'bg-green-50 text-green-600 border border-green-100'
                            }`}
                        >
                          {row.transaction_name}
                        </span>
                      </div>

                      <div className="text-gray-600 text-xs leading-snug text-left line-clamp-2">
                        {row.description}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 bg-white border rounded-xl text-gray-400 font-medium">
                  No transactions found matching your search
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile/Tablet Grid View */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {isLoadingTransactions ? (
            <div className="col-span-full flex items-center justify-center h-40">
              <Spinner size="lg" />
            </div>
          ) : filteredData.length > 0 ? (
            filteredData.map((row: any) => (
              <div
                key={row.id}
                className="relative bg-white rounded-2xl shadow border pt-4 hover:shadow-md transition cursor-pointer overflow-hidden flex flex-col h-full"
              >
                {/* Status Badge - Top Right */}
                <div
                  className={`absolute top-4 right-4 text-white text-sm px-2 font-bold py-1 rounded-lg  shadow-sm ${row.transaction_name === 'Debit' ? 'bg-red-400' : 'bg-teal'
                    }`}
                >
                  {row.transaction_name}
                </div>

                {/* Details Section (Pickup/Delivery Style) */}
                <div className="px-4 flex items-center gap-3 mb-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 bg-teal rounded-full" />
                    <div className="w-0.5 h-10 border-l-2 border-dotted border-gray-300" />
                    <div className="w-2.5 h-2.5 bg-gray-400 rounded-full" />
                  </div>

                  <div className="text-gray-700 text-xs overflow-hidden">
                    <div>
                      <p className="font-bold text-sm text-teal ">
                        Transaction Ref
                      </p>
                      <p className="truncate font-medium">{row.payment_reference_number || row.id}</p>
                    </div>
                    <div className="mt-2 text-xs w-full">
                      <p className="font-bold text-sm text-gray-500 ">
                        Service Type
                      </p>
                      <div className="flex items-center justify-between font-medium overflow-hidden w-full gap-2 text-text2">
                        {row.category_name || 'N/A'}
                        {admin && (
                          <span className="text-text3 truncate min-w-0 font-bold">
                            {row.crn_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tracking Number (New for mobile) */}
                <div className="px-10 mb-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tracking Number</p>
                  <div className="text-sm font-semibold mt-0.5">
                    <WalletTrackingNumber
                      trackingNumber={row.tracking_number}
                      categoryName={row.category_name}
                      transactionName={row.transaction_name}
                    />
                  </div>
                </div>

                {/* Amount Section (Weight/Amount Style) */}
                <div className="flex px-4 justify-between text-sm font-medium mb-3 mt-auto">
                  <div className="flex flex-col items-center justify-center">

                    <span className="font-bold">₹{row.amount}</span>
                    <span className="text-text3 text-sm  font-bold">
                      Amount
                    </span>
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-teal font-bold">
                      ₹{row.usuable_balance || '0'}
                    </span>
                    <span className="text-text3 text-sm  font-bold">
                      Balance
                    </span>
                  </div>
                </div>

                {/* Footer (Simple border-t style) */}
                <div className="px-4 mt-auto py-3 border-t border-gray-50 flex justify-between items-center bg-gray-50/30">
                  <span className="font-bold  text-sm tracking-wider text-gray-400">
                    Date
                  </span>
                  <span className="text-gray-400 text-sm font-bold">
                    {formatWalletDate(row.transaction_date_time)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-10 bg-white border border-dashed rounded-xl text-gray-400 font-bold">
              No transactions found
            </div>
          )}
        </div>

        {/* Pagination bar matching Orders pattern */}
        {!isLoadingTransactions && filteredData.length > 0 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-6">
            <div className="text-sm text-gray-600">
              Showing page {currentPage} of {totalPages || 1}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-32">
                <Select
                  options={pageSizeOptions}
                  value={String(rowsPerPage)}
                  position="top"
                  className="h-10 rounded-xl"
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(prev + 1, Math.max(totalPages, 1))
                  )
                }
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        <Modal />
      </div>
    </div>
  );
}
